import React, { useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import { DossierSummaryResponseDto } from 'lib_api/lib/api/gen/api';

import { Table } from 'components/WrappedComponents';
import {
  ColumnProps,
  FetchDataFunction,
} from 'components/WrappedComponents/Table/types';
import { useAdminFonctionnelFilterContext } from 'hooks/AdminFonctionnelFilterStoreContext';
import {
  SortOptions,
  useAdminSearchDossierSummaries,
} from 'hooks/dossiers/useSearchDossierSummaries';
import { usePaginationContext } from 'hooks/PaginationStoreContext';
import { useSortContext } from 'hooks/SortStoreContext';

import { DashboardAdminFonctionnelFilter } from '../types';

import { buildColumns, buildHeader } from './utils';

function DashboardTable(): React.ReactElement {
  const { pathname } = useLocation();
  const [filter, setfilter, adminActionsRequises] =
    useAdminFonctionnelFilterContext();
  const [sort, setSort] = useSortContext();
  const searchDossierSummaries = useAdminSearchDossierSummaries();
  const [pagination, setPagination] = usePaginationContext('paginationDossier');

  /**
   * Fetch dossiers based on search filters
   */
  const fetchData: FetchDataFunction<
    DossierSummaryResponseDto,
    DashboardAdminFonctionnelFilter,
    SortOptions
  > = useMemo(
    () => (pagination, sorters, filter) =>
      searchDossierSummaries(pagination, sorters, {
        statutDossierEnumList: filter.statut,
        classementList: filter.classement,
        idFourriereList: filter.fourriere,
        listUniteFo: filter.idUniteFOList,
        idJuridictionList: filter.idJuridictionList,
        idNatureBrancheList: filter.natureMiseEnFourriere,
        dateRangeMiseEnFourriere: filter.dateRangeMiseEnFourriere,
        dateRangeBonEnlevement: filter.dateRangeBonEnlevement,
        dateRangeSortieDefinitive: filter.dateRangeSortieDefinitive,
        dateRangeOrdreDestruction: filter.dateRangeOrdreDestruction,
        numeroRecherche: filter.search,
        includeDossiersSortis: filter.includeDossiersSortis,
        idMotifSortieList: filter.motifDeSortie,
        statutsFacturation: filter.statutsTraitementFacturation,
        actionsRequises: filter.actionsRequises,
        includeDossiersAnonymises: filter.includeDossiersAnonymises,
        isDetruitApresSortie: filter.includeOnlyNotDetruitApresSorti
          ? false
          : undefined,
        marque: filter.idMarque,
        modele: filter.idModele,
      }),
    [searchDossierSummaries],
  );

  const columns: ColumnProps<DossierSummaryResponseDto, SortOptions>[] =
    React.useMemo(
      () => buildColumns(pathname, adminActionsRequises),
      [pathname, adminActionsRequises],
    );

  return (
    <div className="table-container">
      <Table
        columns={columns}
        header={buildHeader}
        fetchData={fetchData}
        buildItemKey={(item): string => item.id}
        pageSizeOptions={[10, 20, 50]}
        pagination={pagination}
        setPagination={setPagination}
        filter={filter}
        setFilter={setfilter}
        sort={sort}
        setSort={setSort}
      />
    </div>
  );
}

export default DashboardTable;
